import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ScoreBadge, TagChip } from '../components/Badges';
import { fmtRelative, SOURCE_LABELS } from '../lib/constants';
import { Plus, Search, Filter, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import StageTransitionModal, { STAGES_WITH_MODAL } from '../components/StageTransitionModal';
import { logger } from '../lib/logger';
import { useStageLabel, STAGE_ORDER } from '../lib/stageLabels';

// Set of stage keys that we know how to translate client-side via stageLabels.js.
// For any column key NOT in this set, we fall back to the backend-provided label
// (useful when the pipeline v2 backend introduces custom columns).
const KNOWN_STAGE_KEYS = new Set(STAGE_ORDER);

export default function PipelinePage() {
  const { t } = useTranslation();
  const stageLabel = useStageLabel();
  const [data, setData] = useState(null);
  const [profileFilter, setProfileFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [modal, setModal] = useState({ open: false, lead: null, toStage: null, toStageLabel: null });
  const nav = useNavigate();

  // ── Profile filter options (localized) ─────────────────────────────
  const PROFILE_FILTER_OPTIONS = useMemo(() => ([
    { key: '', label: t('pipeline.profiles.all', 'Tutti i profili') },
    { key: 'equity', label: t('pipeline.profiles.equity', '💰 Equity Share') },
    { key: 'real_estate', label: t('pipeline.profiles.real_estate', '🏠 Real Estate') },
    { key: 'both', label: t('pipeline.profiles.both', '🌐 Equity + Real Estate') },
  ]), [t]);

  const PROFILE_BADGE = useMemo(() => ({
    equity: { label: t('pipeline.profiles.equityBadge', 'Equity'), bg: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    real_estate: { label: t('pipeline.profiles.realEstateBadge', 'Real Estate'), bg: 'bg-amber-100 text-amber-800 border-amber-300' },
    both: { label: t('pipeline.profiles.bothBadge', 'Both'), bg: 'bg-violet-100 text-violet-800 border-violet-300' },
  }), [t]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = profileFilter ? `?profile_type=${profileFilter}` : '';
      const r = await api.get(`/pipeline/board${params}`);
      setData(r.data);
    } catch (err) {
      logger.error('PipelinePage: load board failed', err);
      if (!silent) toast.error(t('pipeline.toasts.loadError', 'Errore caricamento pipeline'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [profileFilter, t]);

  useEffect(() => { load(); }, [load]);

  // ★ 2026-08-14 (Fase 54) — Real-time sync 15s per pipeline: quando un
  // associate/senior sposta un lead di stage, gli altri vedono l'update.
  // ★ Round 2 qualità — dep su `load` (stabile via useCallback): fix hook deps.
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load({ silent: true });
    }, 15000);
    const onVis = () => {
      if (document.visibilityState === 'visible') load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  // ── Localize stage label helper: prefer client-side i18n; fallback backend ─
  const localizeColumnLabel = useCallback((col) => {
    if (!col) return '';
    if (col.key && KNOWN_STAGE_KEYS.has(col.key)) {
      // Backend già include emoji nella label, ma preferiamo il nostro helper
      // (che unisce emoji standardizzato + traduzione i18n).
      return stageLabel(col.key, { emoji: true });
    }
    return col.label || col.key || '';
  }, [stageLabel]);

  // ── Direct transition (no modal) ───────────────────────────────────────
  const doTransition = async (leadId, toStage, metadata = {}) => {
    try {
      await api.post(`/leads/${leadId}/transition`, { to_stage: toStage, metadata });
      toast.success(t('pipeline.toasts.stageUpdated', 'Stage aggiornato'));
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const fallback = t('pipeline.toasts.stageUpdateError', 'Errore aggiornamento stage');
      const msg = typeof detail === 'string' ? detail : fallback;
      toast.error(msg);
    }
  };

  // ── Drag & drop ──────────────────────────────────────────────────────
  const onDragStart = (e, lead) => {
    setDraggingId(lead.id);
    e.dataTransfer.setData('text/plain', JSON.stringify({ leadId: lead.id }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDropToColumn = (e, col) => {
    e.preventDefault();
    setDraggingId(null);
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData('text/plain'));
    } catch {
      return;
    }
    if (!payload?.leadId) return;
    const lead = data?.columns?.flatMap((c) => c.items || []).find((l) => l.id === payload.leadId);
    if (!lead) return;
    if (lead.stage === col.key) return; // No-op if dropping onto same column

    if (STAGES_WITH_MODAL.has(col.key)) {
      // Open modal to capture required metadata before transition.
      // toStageLabel is used only for the modal title; use localized version.
      setModal({
        open: true,
        lead,
        toStage: col.key,
        toStageLabel: localizeColumnLabel(col),
      });
    } else {
      // Direct transition (no metadata required)
      doTransition(lead.id, col.key);
    }
  };

  const handleModalConfirm = async ({ to_stage, metadata }) => {
    await doTransition(modal.lead.id, to_stage, metadata);
  };

  // ── Lead card filtering ──────────────────────────────────────────────
  const filterFn = (l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (l.name || '').toLowerCase().includes(q)
      || (l.email || '').toLowerCase().includes(q)
      || (l.phone || '').toLowerCase().includes(q)
    );
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-border bg-[var(--surface-1)] flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="pipeline-title">
            {t('pipeline.unifiedTitle', 'Pipeline Unificata')}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('pipeline.unifiedSubtitle', 'Trascina i lead tra le fasi. Click per aprire il dettaglio.')}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('pipeline.searchPlaceholder', 'Cerca nome / email / telefono…')}
              className="pl-8 w-56 md:w-64"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="pipeline-search-input"
            />
          </div>
          <Select value={profileFilter || 'all'} onValueChange={(v) => setProfileFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-56" data-testid="pipeline-profile-filter">
              <Filter className="w-4 h-4 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pipeline.profiles.all', 'Tutti i profili')}</SelectItem>
              {PROFILE_FILTER_OPTIONS.filter((p) => p.key).map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="secondary" size="sm" onClick={load} data-testid="pipeline-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> {t('pipeline.refresh', 'Aggiorna')}
          </Button>
          <Button size="sm" onClick={() => nav('/app/leads/new')} data-testid="pipeline-new-lead-button">
            <Plus className="w-4 h-4 mr-1" /> {t('pipeline.newLead', 'Nuovo lead')}
          </Button>
        </div>
      </div>

      {/* Funnel summary bar (counts at a glance) */}
      {data?.columns && (
        <div className="px-4 md:px-6 py-2 border-b border-border bg-[var(--surface-2)]/40 flex items-center gap-3 overflow-x-auto">
          {data.columns.map((c) => (
            <div
              key={c.key}
              className="flex items-center gap-1.5 text-[11px] whitespace-nowrap"
              data-testid={`funnel-summary-${c.key}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: c.color }}
              />
              <span className="text-muted-foreground">{localizeColumnLabel(c)}</span>
              <span className="font-mono font-medium">{c.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Kanban */}
      <div className="flex-1 overflow-x-auto kanban-scroll px-4 md:px-6 py-4">
        <div className="flex gap-3 min-h-full">
          {loading && (
            <div className="text-sm text-muted-foreground p-4">{t('pipeline.loading', 'Caricamento…')}</div>
          )}
          {data?.columns?.map((col) => {
            const items = (col.items || []).filter(filterFn);
            const localizedLabel = localizeColumnLabel(col);
            return (
              <div
                key={col.key}
                className="min-w-[280px] max-w-[300px] flex flex-col rounded-[var(--radius)] bg-[var(--surface-2)] border border-border"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={(e) => onDropToColumn(e, col)}
                data-testid={`kanban-column-${col.key}`}
              >
                <div
                  className="px-3 py-2.5 border-b border-border flex items-center justify-between sticky top-0 rounded-t-[var(--radius)] bg-[var(--surface-2)]"
                  style={{ borderTop: `3px solid ${col.color}` }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm font-medium truncate">{localizedLabel}</div>
                  </div>
                  <div
                    className="text-[11px] rounded-full px-1.5 py-0.5 border bg-[var(--surface-1)] border-border font-mono"
                    data-testid={`kanban-count-${col.key}`}
                  >
                    {items.length}
                  </div>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                  {items.length === 0 && (
                    <div className="text-[11px] text-muted-foreground italic px-2 py-3">
                      {t('pipeline.emptyColumn', 'Nessun lead.')}
                    </div>
                  )}
                  {items.map((l) => {
                    const cf = l.custom_fields || {};
                    const profile = cf.profile_type;
                    const profileBadge = profile ? PROFILE_BADGE[profile] : null;
                    return (
                      <Card
                        key={l.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, l)}
                        onClick={() => nav(`/app/leads/${l.id}`)}
                        className={`cursor-pointer border-border bg-card hover:shadow-md transition-shadow ${
                          draggingId === l.id ? 'opacity-60' : ''
                        }`}
                        data-testid="kanban-lead-card"
                      >
                        <div className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-medium text-sm leading-tight truncate" title={l.name}>
                              {l.name}
                            </div>
                            <ScoreBadge score={l.score} />
                          </div>
                          {(l.email || l.phone) && (
                            <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                              {l.email || l.phone}
                            </div>
                          )}
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            {profileBadge && (
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded-full border ${profileBadge.bg}`}
                                data-testid={`profile-badge-${profile}`}
                              >
                                {profileBadge.label}
                              </span>
                            )}
                            <span className="text-[10px] text-muted-foreground">
                              {SOURCE_LABELS[l.source] || l.source}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              · {fmtRelative(l.updated_at || l.created_at)}
                            </span>
                          </div>
                          {cf.assigned_associate_name && (
                            <div className="mt-1.5 text-[10px] text-emerald-700 truncate">
                              📞 {cf.assigned_associate_name}
                            </div>
                          )}
                          {cf.senior_agent_name && (
                            <div className="text-[10px] text-amber-700 truncate">
                              🎯 {cf.senior_agent_name}
                            </div>
                          )}
                          {l.tags?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {l.tags.slice(0, 3).map((tag) => (
                                <TagChip key={tag} tagKey={tag} />
                              ))}
                              {l.tags.length > 3 && (
                                <span className="text-[10px] text-muted-foreground">+{l.tags.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage transition modal */}
      <StageTransitionModal
        open={modal.open}
        onOpenChange={(open) => setModal((m) => ({ ...m, open }))}
        lead={modal.lead}
        toStage={modal.toStage}
        toStageLabel={modal.toStageLabel}
        onConfirm={handleModalConfirm}
      />
    </div>
  );
}
